import { Location } from '@angular/common';
import { inject, provideAppInitializer } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Recovery from stale lazy chunks after a web deploy.
 *
 * A tab opened before a deploy keeps the old `index.html`/`main-*.js` in memory, which
 * point at `chunk-<hash>.js` files the new deploy has removed. The next lazy route then
 * fails with "Failed to fetch dynamically imported module" (or the Safari/Firefox
 * equivalents). A full reload fetches the new `index.html` and fixes it.
 *
 * To avoid reload loops (e.g. the chunk is really missing), a timestamp is stored in
 * sessionStorage: only one automatic reload is attempted until a navigation succeeds
 * or `RELOAD_GUARD_MS` has elapsed.
 *
 * Usage (web apps only; Capacitor bundles its chunks):
 *   provideRouter(routes, withNavigationErrorHandler(reloadOnStaleChunkError)),
 *   provideStaleChunkReloadReset(),
 */

const STORAGE_KEY = 'dms:stale-chunk-reload';
const RELOAD_GUARD_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i, // Chromium
  /Importing a module script failed/i, // Safari
  /error loading dynamically imported module/i, // Firefox
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
];

export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: unknown }).name;
  if (name === 'ChunkLoadError') return true;
  const message =
    typeof error === 'string'
      ? error
      : String((error as { message?: unknown }).message ?? '');
  return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
}

function readMarker(): number | null {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  const ts = raw ? Number(raw) : NaN;
  return Number.isFinite(ts) ? ts : null;
}

function clearMarker(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // sessionStorage unavailable: nothing to clear.
  }
}

/**
 * Handler for `withNavigationErrorHandler`. Runs in the router's injection context.
 * On a chunk-load error it does a single full reload to the URL of the failed navigation.
 * The error keeps propagating as usual (Sentry still records it).
 */
export function reloadOnStaleChunkError(event: NavigationError): void {
  if (!isChunkLoadError(event.error)) return;

  const target = inject(Location).prepareExternalUrl(event.url);
  try {
    const last = readMarker();
    if (last !== null && Date.now() - last < RELOAD_GUARD_MS) {
      // Already reloaded recently and it still fails: do not loop.
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // Without sessionStorage there is no loop protection: do not reload.
    return;
  }
  window.location.assign(target);
}

/** Clears the reload marker after every successful navigation. */
export function provideStaleChunkReloadReset() {
  return provideAppInitializer(() => {
    inject(Router)
      .events.pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => clearMarker());
  });
}
